import { useState } from 'react';
import { twMerge } from '@belong/common';
import { IconLeftCaret, IconPlay, IconRightCaret } from '@belong/icons';
import { ButtonBase, Image, MediaGalleryModal, Text } from '@belong/ui';
import type {
  ImprovementsProgressItemModel,
  MaintenanceMediaModel,
} from 'api/models';
import ReactPlayer from 'react-player/lazy';
import { POST_INSPECTION_FLOW_STRINGS } from 'strings/post-inspection-flow';
import { getDisplayableMedia, groupMediaByStage } from '../../utils';
import { RepairPlaceholder } from '../repair-placeholder/repair-placeholder';

type Props = {
  media: ImprovementsProgressItemModel['media'];
};

type StageKey = 'before' | 'during' | 'after';

const STAGE_LABEL_CLASSNAMES: Record<StageKey, string> = {
  before: 'text-dark-gray',
  during: 'text-blue',
  after: 'text-green',
};

const STAGE_LABELS: Record<StageKey, string> = {
  before: POST_INSPECTION_FLOW_STRINGS['progress.stage.before'],
  during: POST_INSPECTION_FLOW_STRINGS['progress.stage.during'],
  after: POST_INSPECTION_FLOW_STRINGS['progress.stage.after'],
};

function MediaAsset({ item }: { item: MaintenanceMediaModel }) {
  if (!item.url) return null;

  if (item.mediaType === 'Video') {
    return (
      <ReactPlayer
        url={[{ src: item.url, type: 'video/mp4' }]}
        muted
        controls
        width="100%"
        height="100%"
      />
    );
  }

  return <Image alt="" src={item.url} className="object-cover" />;
}

function MediaThumbnailAsset({ item }: { item: MaintenanceMediaModel }) {
  if (!item.url) return null;

  if (item.mediaType === 'Video') {
    return (
      <div className="flex h-full w-full items-center justify-center bg-dark-navy">
        <IconPlay width={12} height={12} className="text-white" />
      </div>
    );
  }

  return <Image alt="" src={item.url} className="object-cover" />;
}

function MediaStageColumn({
  stage,
  items,
}: {
  stage: StageKey;
  items: MaintenanceMediaModel[];
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const galleryItems = items.filter(
    (
      item,
    ): item is MaintenanceMediaModel & {
      url: string;
      mediaType: 'Video' | 'Image' | 'Pdf';
    } =>
      Boolean(item.url) &&
      (item.mediaType === 'Image' || item.mediaType === 'Video'),
  );
  const mainItem = items[selectedIndex] ?? items[0];

  return (
    <div className="min-w-0">
      <Text
        variant="body"
        fontWeight="semibold"
        className={STAGE_LABEL_CLASSNAMES[stage]}
      >
        {STAGE_LABELS[stage]}
      </Text>

      <div className="mt-2xs aspect-h-1 aspect-w-1 w-full">
        <div className="overflow-hidden rounded-small border border-solid border-gray">
          {mainItem ? (
            mainItem.mediaType === 'Video' ? (
              <MediaAsset item={mainItem} />
            ) : (
              <ButtonBase
                aria-label={`${STAGE_LABELS[stage]} photo, open full screen`}
                className="block h-full w-full"
                onClick={() => setIsGalleryOpen(true)}
              >
                <MediaAsset item={mainItem} />
              </ButtonBase>
            )
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-light-gray">
              <RepairPlaceholder
                size={60}
                className={stage === 'before' ? 'grayscale' : undefined}
              />
            </div>
          )}
        </div>
      </div>

      {items.length > 1 && (
        <div className="mt-sm flex items-center justify-between">
          <ButtonBase
            aria-label={POST_INSPECTION_FLOW_STRINGS['progress.media.previous']}
            className="shrink-0 text-navy disabled:text-gray"
            disabled={selectedIndex === 0}
            onClick={() => setSelectedIndex((index) => Math.max(index - 1, 0))}
          >
            <IconLeftCaret width={8} height={15} />
          </ButtonBase>

          <div className="flex items-center gap-xs">
            {items.map((item, index) => (
              <ButtonBase
                key={item.uniqueId}
                aria-label={`${STAGE_LABELS[stage]} ${
                  item.mediaType === 'Video' ? 'video' : 'photo'
                } ${index + 1}`}
                aria-pressed={index === selectedIndex}
                className={twMerge(
                  'block h-[26px] w-[26px] overflow-hidden rounded-[2px] bg-gray',
                  index === selectedIndex &&
                    'border-3 border-solid border-navy',
                )}
                onClick={() => setSelectedIndex(index)}
              >
                <MediaThumbnailAsset item={item} />
              </ButtonBase>
            ))}
          </div>

          <ButtonBase
            aria-label={POST_INSPECTION_FLOW_STRINGS['progress.media.next']}
            className="shrink-0 text-navy disabled:text-gray"
            disabled={selectedIndex === items.length - 1}
            onClick={() =>
              setSelectedIndex((index) => Math.min(index + 1, items.length - 1))
            }
          >
            <IconRightCaret width={8} height={15} />
          </ButtonBase>
        </div>
      )}

      {galleryItems.length > 0 && (
        <MediaGalleryModal
          isOpen={isGalleryOpen}
          media={galleryItems}
          selectedIndex={selectedIndex}
          onClose={() => setIsGalleryOpen(false)}
        />
      )}
    </div>
  );
}

export function ProgressItemMedia({ media }: Props) {
  const displayableMedia = getDisplayableMedia(media);

  if (displayableMedia.length === 0) return null;

  const grouped = groupMediaByStage(displayableMedia);

  const laterStage =
    grouped.during.length > 0 && grouped.after.length === 0
      ? 'during'
      : 'after';

  const columns = (['before', laterStage] as const).map((stage) => ({
    stage,
    items: grouped[stage],
  }));

  return (
    <div className="mt-sm grid grid-cols-1 gap-sm md:grid-cols-2">
      {columns.map((column) => (
        <MediaStageColumn
          key={column.stage}
          stage={column.stage}
          items={column.items}
        />
      ))}
    </div>
  );
}
